import { useEffect, useMemo } from 'react';

import { useForm, useFormState } from 'react-final-form';

import { parseApiError } from '@cast/api-client';
import {
  Button,
  DrawerContent,
  DrawerFooter,
  notify,
  useDrawerInstance,
  Stack,
} from '@cast/design-system';
import { useAbility } from '@cast/shared-ability-data-access';

import { useCreateEdgeNodeMutation } from 'hooks/mutations/omni';
import { useListEdgeConfigurationsQuery } from 'hooks/queries/omni';

import { buildNodeConfig } from './buildNodeConfig';
import { getSelectedInstance, mapInstanceArchitectureToEdge } from './_utils';
import { useProvisionNodesFilterInstanceTypesQuery } from './useProvisionNodesFilterInstanceTypesQuery';
import { PROVISION_NODES_CLOUD_INITIAL_VALUES } from '../constants';
import { ProvisionNodesCloudConfigurationSlideForm } from './ProvisionNodesCloudConfigurationSlideForm';
import { validate } from './validate.schema';
import { useProvisionNodesDrawerContext } from '../ProvisionNodesDrawerContext';
import { ProvisionNodesCloudConfigurationFormValues } from '../types';

import { RffForm } from '@cast/shared-rff-ui';
import { EnhancedEdgeLocation } from '../../../types';

const EDGE_LOCATION_SELECTOR_HEIGHT = 107;
const FOOTER_HEIGHT = 65;

const CloudConfigurationSlideFooter = () => {
  const { close, setOnCloseGuard, setShouldRenderGuard } = useDrawerInstance();
  const form = useForm<ProvisionNodesCloudConfigurationFormValues>();
  const { dirty, submitting } = useFormState({
    subscription: { dirty: true, submitting: true },
  });
  const canCreateNode = useAbility().can('create', 'ClusterNodes');

  // Enable guard when form is dirty
  useEffect(() => {
    setOnCloseGuard(dirty);
  }, [dirty, setOnCloseGuard]);

  const handleCancel = () => {
    if (dirty) {
      setShouldRenderGuard(true);
    } else {
      close();
    }
  };

  const handleClickProvision = () => {
    form.submit();
  };

  return (
    <DrawerFooter sx={{ justifyContent: 'flex-end' }}>
      <Stack direction="row" gap={16}>
        <Button
          variant="secondary"
          size="md"
          onClick={handleCancel}
          disabled={submitting}
          testId="cancel-button"
        >
          Cancel
        </Button>
        <Button
          size="md"
          disabled={!canCreateNode || submitting}
          loading={submitting}
          onClick={handleClickProvision}
          testId="provision-button"
        >
          Provision
        </Button>
      </Stack>
    </DrawerFooter>
  );
};

type Props = {
  edgeLocation: EnhancedEdgeLocation;
};

export const ProvisionNodesCloudConfigurationSlide = ({
  edgeLocation,
}: Props) => {
  const {
    clusterId,
    cloudConfigValue,
    hasEdgeLocationPredefined,
    setCloudConfigValue,
  } = useProvisionNodesDrawerContext();
  const { close } = useDrawerInstance();
  const createEdgeNode = useCreateEdgeNodeMutation();
  const { instanceTypes } = useProvisionNodesFilterInstanceTypesQuery({
    clusterId,
    edgeLocationId: edgeLocation.id,
  });
  const { edgeConfigurations } = useListEdgeConfigurationsQuery({
    clusterId,
    edgeLocationId: edgeLocation.id,
  });

  const availableInstances = useMemo(() => {
    if (!instanceTypes) {
      return [];
    }

    const edgeLocationZones =
      edgeLocation.zones?.map((zone) => zone.name) || [];

    return instanceTypes
      .filter((instanceType) => instanceType.csp === edgeLocation.provider)
      .filter((instanceType) => edgeLocationZones.includes(instanceType.zone));
  }, [instanceTypes, edgeLocation.provider, edgeLocation.zones]);

  const handleSubmit = async (
    values: ProvisionNodesCloudConfigurationFormValues
  ) => {
    if (
      !clusterId ||
      !edgeLocation.id ||
      !values.instanceType ||
      !values.availabilityZone
    ) {
      return;
    }

    setCloudConfigValue(values);

    const selectedInstance = getSelectedInstance({
      availableInstances,
      instanceTypeName: values.instanceType,
      availabilityZone: values.availabilityZone,
    });

    if (!selectedInstance) {
      return;
    }

    const edgeNodeConfig = buildNodeConfig({
      values,
      nodeArchitecture: mapInstanceArchitectureToEdge(selectedInstance),
    });

    try {
      await createEdgeNode.mutateAsync({
        clusterId,
        edgeLocationId: edgeLocation.id,
        edgeNodeConfig,
      });

      notify.success('Node provisioning requested');
      close();
    } catch (error) {
      notify.error(parseApiError(error));
    }
  };

  return (
    <RffForm<ProvisionNodesCloudConfigurationFormValues>
      mode="create"
      array
      sx={{
        height: `calc(100% - ${
          hasEdgeLocationPredefined ? 0 : EDGE_LOCATION_SELECTOR_HEIGHT
        }px)`,
        display: 'flex',
        flexDirection: 'column',
      }}
      initialValues={cloudConfigValue ?? PROVISION_NODES_CLOUD_INITIAL_VALUES}
      validate={validate}
      onSubmit={handleSubmit}
    >
      <DrawerContent
        sx={{
          flex: 1,
          maxHeight: `calc(100% - ${FOOTER_HEIGHT}px)`,
          overflowY: 'auto',
        }}
      >
        <ProvisionNodesCloudConfigurationSlideForm
          edgeLocation={edgeLocation}
          availableInstances={availableInstances}
          edgeConfigurations={edgeConfigurations}
        />
      </DrawerContent>
      <CloudConfigurationSlideFooter />
    </RffForm>
  );
};
